"use client";

import { useState } from "react";
import { TraitCategory } from "@/types/cat";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

interface TraitFilterProps {
  traits: TraitCategory[];
  selected: Record<string, string[]>;
  onChange: (selected: Record<string, string[]>) => void;
}

export function TraitFilter({ traits, selected, onChange }: TraitFilterProps) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const toggleTrait = (categoryKey: string, value: string) => {
    const current = selected[categoryKey] || [];
    const updated = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    onChange({ ...selected, [categoryKey]: updated });
  };

  const toggleExpand = (key: string) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  if (!traits.length) return null;

  return (
    <div className="space-y-4">
      <h4 className="font-pixel text-xs text-muted-foreground">Trait filters</h4>
      {traits.map((category) => {
        const isExpanded = expanded[category.key] ?? true;
        const selectedCount = (selected[category.key] || []).length;

        return (
          <div key={category.key} className="border-b border-border pb-4">
            <button
              onClick={() => toggleExpand(category.key)}
              className="flex w-full items-center justify-between py-2 text-left"
            >
              <span className="font-medium">
                {category.label}
                {selectedCount > 0 && (
                  <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">
                    {selectedCount}
                  </span>
                )}
              </span>
              <ChevronDown
                className={cn(
                  "h-4 w-4 text-muted-foreground transition-transform",
                  !isExpanded && "-rotate-90"
                )}
              />
            </button>

            {isExpanded && (
              <div className="mt-2 flex max-h-48 flex-wrap gap-1.5 overflow-y-auto">
                {category.options.map((option) => {
                  const isSelected = (selected[category.key] || []).includes(
                    option.value
                  );
                  return (
                    <button
                      key={option.value}
                      onClick={() => toggleTrait(category.key, option.value)}
                      className={cn(
                        "rounded-md border px-2 py-1 text-xs transition-colors",
                        isSelected
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-border bg-card text-muted-foreground hover:text-foreground"
                      )}
                      title={`${option.value} (${option.count})`}
                    >
                      {option.value}
                      <span className="ml-1 text-muted-foreground/60">
                        {option.count}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
